// frontend/src/components/TeamPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { teamService } from '../services/supabaseClient';
import PhotoUpload from './PhotoUpload';
import { Toaster } from 'react-hot-toast';
import { getEventDisplayName } from '../config/events';
import { supabase } from '../services/supabaseClient';
import FlagDisplay from './FlagDisplay';
import LightboxModal from './AdminTeamDetail/LightboxModal';
import { formatRelativeTime } from '../utils/dateUtils';

// Team configurations with flag emojis and colors
import { getFlagEmoji } from '../utils/countryFlags';
// import { getEventDbName } from '../config/events';

const TeamPage = ({ eventCode, teamName }) => {
  const currentEventCode = eventCode || 'mjwc2025'; // Default fallback
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('pending'); // 'all', 'pending', 'completed'
  const [teamData, setTeamData] = useState(null);
  const [eventData, setEventData] = useState(null);  // Event from database
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [uploadedPlayerName, setUploadedPlayerName] = useState('');
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const searchInputRef = useRef(null);

  // Focus search on '/' (skip if already typing in an input/textarea)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openLightbox = (player) => {
    setLightboxImage(player);
    setShowLightbox(true);
  };

  // Map event codes to display names
  const eventDisplayName = eventData?.short_name || getEventDisplayName(currentEventCode);

  // Fetch event info first (needed for all queries)
  useEffect(() => {
    const fetchEventInfo = async () => {
      const { data: event } = await supabase
        .from('events')
        .select('*')
        .eq('code', currentEventCode)
        .single();

      if (event) {
        setEventData(event);
      }
    };
    fetchEventInfo();
  }, [currentEventCode]);

  // Fetch team info to get correct casing (only after eventData is loaded)
  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!eventData?.short_name) return;  // Wait for event data

      const { data } = await supabase
        .from('event_uploads')
        .select('team')
        .eq('event_name', eventData.short_name)
        .ilike('team', teamName)
        .limit(1);

      if (data && data.length > 0) {
        setTeamData({
          name: data[0].team,
          flag: getFlagEmoji(data[0].team)
        });
      } else {
        setTeamData({
          name: teamName,
          flag: getFlagEmoji(teamName)
        });
      }
    };

    if (teamName && eventData) fetchTeamInfo();
  }, [teamName, eventData]);

  // Get team configuration
  const team = teamData || {
    name: teamName,
    flag: '🏳️'
  };

  const fetchPlayers = useCallback(async () => {
    if (!eventData?.short_name) return;  // Wait for event data

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await teamService.getPlayersByTeam(
        teamData?.name || teamName,
        eventData.short_name  // Use database event name
      );

      if (error) {
        setError('Failed to load players. Please try again.');
      } else {
        setPlayers(data || []);
        // Auto-select first pending player if none selected
        if (data?.length > 0) {
          const firstPending = data.find(p => !p.upload_status);
          if (firstPending) {
            setSelectedPlayer(firstPending);
          }
        }
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [teamData, teamName, eventData]);

  useEffect(() => {
    // Fetch players once we have teamData and eventData
    if ((teamData || teamName) && eventData) {
      fetchPlayers();
    }
  }, [teamData, teamName, fetchPlayers, eventData]);

  // Filter players based on search and view mode
  const filteredPlayers = useMemo(() => {
    let filtered = players;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(player =>
        player.player_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by view mode
    if (viewMode === 'pending') {
      filtered = filtered.filter(player => !player.upload_status);
    } else if (viewMode === 'completed') {
      filtered = filtered.filter(player => player.upload_status);
    }

    return filtered;
  }, [players, searchQuery, viewMode]);

  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    // Smooth scroll to upload section on mobile
    setTimeout(() => {
      document.getElementById('upload-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleUploadComplete = async (playerId, photoUrl) => {
    // Find the player name for the overlay
    const uploadedPlayer = players.find(p => p.id === playerId);

    // Update player data
    setPlayers(prevPlayers =>
      prevPlayers.map(player =>
        player.id === playerId
          ? { ...player, upload_status: true, photo_url: photoUrl }
          : player
      )
    );

    // IMPORTANT: Update the selected player with new data so PhotoUpload shows updated state
    if (selectedPlayer?.id === playerId) {
      setSelectedPlayer({
        ...selectedPlayer,
        upload_status: true,
        photo_url: photoUrl
      });
    }

    // Set the uploaded player name and show success overlay
    setUploadedPlayerName(uploadedPlayer?.player_name || '');
    setShowSuccessOverlay(true);

    // Was this a fresh upload (player was pending) or a re-upload (replacing an existing photo)?
    const wasPending = !uploadedPlayer?.upload_status;

    // After 1.5 seconds, fade out overlay while transitioning
    setTimeout(() => {
      // Auto-advance only on fresh uploads — re-uploads should stay on the same player
      // so the user can verify the replacement.
      if (wasPending) {
        const nextPending = players.find(p => p.id !== playerId && !p.upload_status);

        if (nextPending) {
          // Stay in pending view, select the next pending player
          setSelectedPlayer(nextPending);
        } else {
          // No more pending — switch to completed view as a "you're done" signal
          setViewMode('completed');
        }
      }
      // If !wasPending (re-upload), do nothing — leave selection and view untouched.

      // Fade out the overlay
      const overlay = document.getElementById('success-overlay');
      if (overlay) {
        overlay.style.opacity = '0';
        setTimeout(() => {
          setShowSuccessOverlay(false);
          setUploadedPlayerName('');
        }, 300); // Wait for fade animation
      }
    }, 1500);
  };

  // Calculate stats
  const stats = {
    total: players.length,
    completed: players.filter(p => p.upload_status).length,
    pending: players.filter(p => !p.upload_status).length
  };

  const completionPercentage = stats.total > 0
    ? Math.round((stats.completed / stats.total) * 100)
    : 0;

  const SuccessOverlay = () => {
    if (!showSuccessOverlay) return null;

    return (
      <div
        id="success-overlay"
        className="fixed inset-0 bg-white flex items-center justify-center transition-opacity duration-300"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          opacity: 1
        }}
      >
        <div className="text-center p-8">
          {/* Success Animation */}
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-bounce">
              <svg
                className="w-12 h-12 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Upload Successful!
          </h2>
          <p className="text-gray-600 mb-4">
            Photo for <span className="font-semibold">{uploadedPlayerName}</span> has been uploaded
          </p>

          {/* Loading dots animation */}
          <div className="flex justify-center gap-1">
            <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          className: '',
          style: {
            zIndex: 9999,
          },
        }}
      />
      {/* Success Overlay */}
      <SuccessOverlay />

      {/* Modern Hero Header - Refined Side-Aligned Layout */}
      <div className="relative overflow-hidden bg-mjwc-primary">
        {/* Deep Gradient Background */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-mjwc-primary via-mjwc-secondary to-indigo-900" />

        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 z-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />

        {/* Content Container */}
        <div className="relative z-20 px-6 py-10 sm:py-16 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

            {/* Left Column: Team Info & Identity */}
            <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
              {/* Event Badge */}
              <div className="inline-flex items-center px-4 py-1.5 mb-6 rounded-full bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
                <span className="text-[10px] sm:text-xs font-black tracking-[0.2em] text-white uppercase">
                  {eventDisplayName}
                </span>
              </div>

              {/* Team Title & Flag */}
              <div className="flex flex-col lg:flex-row items-center lg:items-end gap-5 mb-8">
                <div className="relative">
                  <div className="absolute -inset-1 bg-white/30 rounded-full blur-md animate-pulse" />
                  <div className="relative bg-white/10 p-1.5 rounded-full backdrop-blur-md border border-white/20 shadow-2xl">
                    <FlagDisplay teamName={team.name} size="xl" />
                  </div>
                </div>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight leading-none drop-shadow-2xl">
                  {team.name}
                </h1>
              </div>

              {/* Glassmorphism Stats Card - Integrated Left */}
              <div className="max-w-md mx-auto lg:mx-0 bg-white/10 backdrop-blur-2xl rounded-3xl border border-white/20 p-6 shadow-2xl">
                {/* Progress */}
                <div className="mb-6">
                  <div className="flex justify-between items-end mb-2.5">
                    <span className="text-white text-[10px] font-black uppercase tracking-widest opacity-70">Sync Status</span>
                    <span className="text-white text-xl font-black">{completionPercentage}%</span>
                  </div>
                  <div className="h-2.5 bg-white/10 rounded-full overflow-hidden border border-white/5">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 shadow-[0_0_15px_rgba(52,211,153,0.6)] transition-all duration-1000 ease-out"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Stat Tab Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'all', label: 'Total', value: stats.total, color: 'bg-white/10' },
                    { id: 'completed', label: 'Done', value: stats.completed, color: 'bg-emerald-500' },
                    { id: 'pending', label: 'Pending', value: stats.pending, color: 'bg-amber-500' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setViewMode(tab.id)}
                      className={`flex flex-col items-center py-3 px-1 rounded-2xl transition-all duration-300 transform ${viewMode === tab.id
                        ? `${tab.id === 'all' ? 'bg-white text-mjwc-primary' : `${tab.color} text-white`} shadow-[0_8px_20px_rgba(0,0,0,0.3)] -translate-y-1 scale-105`
                        : 'bg-white/5 text-white hover:bg-white/10 border border-white/5'
                        }`}
                    >
                      <span className="text-2xl font-black leading-none mb-1">{tab.value}</span>
                      <span className="text-[9px] font-bold uppercase tracking-widest opacity-60">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Event Thumbnail - Visual Hero */}
            {eventData?.thumbnail_url && (
              <div className="flex-1 w-full max-w-sm lg:max-w-md order-1 lg:order-2">
                <div className="relative group">
                  {/* Outer Glow */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-mjwc-primary to-cyan-500 rounded-[2rem] opacity-20 blur-3xl group-hover:opacity-30 transition duration-1000" />

                  {/* Image Frame */}
                  <div className="relative aspect-[4/3] overflow-hidden rounded-[2rem] border border-white/10 shadow-3xl transform rotate-1 group-hover:rotate-0 transition-all duration-700">
                    <img
                      src={eventData.thumbnail_url}
                      alt={eventDisplayName}
                      className="w-full h-full object-cover transform scale-105 group-hover:scale-100 transition duration-700"
                    />
                    {/* Glass Overlay on Image Bottom */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-12">
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Wave Divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none transform rotate-180 translate-y-[1px]">
          <svg className="relative block w-full h-[40px] lg:h-[60px] fill-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-4 py-6">
        {loading ? (
          // Loading State
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mjwc-primary mb-4"></div>
            <p className="text-gray-600">Loading players...</p>
          </div>
        ) : error ? (
          // Error State
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchPlayers}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : players.length === 0 ? (
          // No Players State
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <p className="text-yellow-700 text-lg">No players found for this team.</p>
          </div>
        ) : (
          <>
            {/* Search and Filter Section - Sticky */}
            <div className="sticky top-0 z-30 bg-white/60 backdrop-blur-xl border-b border-white/40 shadow-[0_4px_20px_rgba(0,0,0,0.04)] -mx-4 px-4 py-3 mb-6 space-y-4 transition-all duration-300 overflow-hidden">
              {/* Mini progress strip — at-a-glance progress when scrolled */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200/40">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all duration-700 ease-out"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              {/* Search Bar */}
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search player name... (Press '/')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 pr-4 text-gray-700 bg-white/60 backdrop-blur-md border-2 border-white/40 rounded-lg focus:outline-none focus:border-mjwc-primary focus:bg-white focus:ring-2 focus:ring-mjwc-primary/20 transition-all"
                />
                <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="flex gap-2 p-1 bg-white/40 backdrop-blur-md border border-white/30 rounded-lg shadow-sm">
                <button
                  onClick={() => setViewMode('all')}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${viewMode === 'all'
                    ? 'bg-white text-mjwc-primary shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Total ({stats.total})
                </button>
                <button
                  onClick={() => setViewMode('completed')}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${viewMode === 'completed'
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Done ({stats.completed})
                </button>
                <button
                  onClick={() => setViewMode('pending')}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${viewMode === 'pending'
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Pending ({stats.pending})
                </button>
              </div>
            </div>

            {/* Players List */}
            <div className="mb-8">
              {filteredPlayers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No players found matching your criteria.
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredPlayers.map((player) => (
                    <div
                      key={player.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handlePlayerSelect(player)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePlayerSelect(player);
                        }
                      }}
                      className={`
                        relative p-4 rounded-lg border-2 text-left transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-mjwc-primary
                        ${selectedPlayer?.id === player.id
                          ? 'border-mjwc-primary bg-blue-50 shadow-md scale-[0.98]'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {player.player_name}
                          </p>
                          {(player.first_name || player.last_name) && (
                            <p className="text-xs text-gray-500 truncate">
                              {[player.first_name, player.last_name].filter(Boolean).join(' ')}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {player.upload_status
                              ? (player.updated_at ? `Uploaded · ${formatRelativeTime(player.updated_at)}` : 'Uploaded')
                              : 'Needs Photo'}
                          </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                          {player.upload_status ? (
                            player.photo_url ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLightbox(player);
                                }}
                                className="block w-10 h-10 rounded-full overflow-hidden hover:ring-2 hover:ring-mjwc-primary hover:ring-offset-2 transition-all focus:outline-none focus:ring-2 focus:ring-mjwc-primary focus:ring-offset-2"
                                title="View photo"
                                aria-label={`View photo of ${player.player_name}`}
                              >
                                <img
                                  src={player.photo_url}
                                  alt={player.player_name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </button>
                            ) : (
                              // Fallback to checkmark if no photo_url for some reason
                              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedPlayer?.id === player.id && (
                        <div className="absolute -inset-0.5 bg-mjwc-primary/20 rounded-lg animate-pulse -z-10" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Section */}
            {selectedPlayer && (
              <div id="upload-section" className="scroll-mt-20">
                <div className="bg-white rounded-xl shadow-lg border-2 border-mjwc-primary/20 p-6">
                  {/* Only show the header if no photo exists */}
                  {!selectedPlayer.upload_status && (
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Uploading for: {selectedPlayer.player_name}
                      </h3>
                    </div>
                  )}
                  <PhotoUpload
                    key={selectedPlayer?.id}
                    player={selectedPlayer}
                    eventCode={currentEventCode}
                    onUploadComplete={handleUploadComplete}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox for viewing player photos */}
      <LightboxModal
        show={showLightbox}
        image={lightboxImage}
        onClose={() => setShowLightbox(false)}
      />

      {/* Quick Actions FAB (Floating Action Button) for mobile */}
      {!loading && players.length > 0 && stats.pending > 0 && (
        <button
          onClick={() => {
            const firstPending = players.find(p => !p.upload_status);
            if (firstPending) handlePlayerSelect(firstPending);
          }}
          className="fixed bottom-6 right-6 w-14 h-14 bg-mjwc-primary text-white rounded-full shadow-lg hover:bg-mjwc-secondary transition-all hover:scale-110 flex items-center justify-center z-30 sm:hidden"
          aria-label="Jump to next pending player"
          title="Jump to next pending player"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default TeamPage;